/**
 * returnRequestTransform.js
 * Pure helper for transforming SAP Business One return request data
 *
 * Business rules:
 * - For each Sales Order line:
 *   A) If fully delivered (deliveredQty == orderedQty):
 *      -> show EXACTLY ONE row (collapsed)
 *      -> qtyAvailableForReturn = delivered qty capped by returnable balance
 *      -> If deliveries from multiple DNs, display "Multiple DNs"
 *   B) If not fully delivered:
 *      -> show MULTIPLE rows:
 *        1) one row per DN delivery portion
 *        2) one row for remaining undelivered qty (if > 0)
 */

/**
 * Format a date value to YYYY-MM-DD or empty string
 */
const formatDate = (value) => {
    if (!value) return "";
    const text = String(value).trim();
    if (!text) return "";
    if (text.length >= 10) return text.substring(0, 10);
    return text;
};

/**
 * Safe number conversion
 */
const toNumber = (value) => {
    const num = parseFloat(value);
    return isNaN(num) ? 0 : num;
};

/**
 * Safe nullable number conversion
 */
const toNullableNumber = (value) => {
    if (value === undefined || value === null) return null;
    if (typeof value === "string" && value.trim() === "") return null;
    const num = parseFloat(value);
    return isNaN(num) ? null : num;
};

/**
 * Safe boolean conversion
 */
const toBoolean = (value, fallback = null) => {
    if (typeof value === "boolean") return value;
    if (typeof value === "number") {
        if (value === 1) return true;
        if (value === 0) return false;
    }
    if (typeof value === "string") {
        const normalized = value.trim().toUpperCase();
        if (!normalized) return fallback;
        if (["1", "Y", "YES", "TRUE", "T"].includes(normalized)) return true;
        if (["0", "N", "NO", "FALSE", "F"].includes(normalized)) return false;
    }
    return fallback;
};

/**
 * Check if value is present (not null/undefined/blank string)
 */
const hasValue = (value) => {
    if (value === undefined || value === null) return false;
    if (typeof value === "string") return value.trim() !== "";
    return true;
};

/**
 * Pick first non-empty value by ordered keys
 */
const firstValue = (source, keys, fallback = null) => {
    if (!source || !Array.isArray(keys)) return fallback;
    for (let i = 0; i < keys.length; i += 1) {
        const key = keys[i];
        if (!Object.prototype.hasOwnProperty.call(source, key)) continue;
        const value = source[key];
        if (hasValue(value)) return value;
    }
    return fallback;
};

/**
 * Resolve delivered returnable quantity by respecting RequestedReturnQty.
 * Priority:
 * - AllowedReturnOpenQty (minus RequestedReturnQty when provided)
 * - ReturnableQty (as an upper cap)
 * - Fallback derived from delivered/open quantity and CreditNoteReturnQty
 */
const resolveDeliveredReturnableQty = (source = {}, fallbackQty = 0) => {
    const baseQty = Math.max(0, toNumber(fallbackQty));
    const requestedReturnQty = Math.max(
        0,
        toNullableNumber(firstValue(source, ["RequestedReturnQty"], null)) || 0
    );
    const creditNoteReturnQty = Math.max(
        0,
        toNullableNumber(firstValue(source, ["CreditNoteReturnQty"], null)) || 0
    );
    const allowedReturnOpenQty = toNullableNumber(firstValue(source, ["AllowedReturnOpenQty"], null));
    const returnableQty = toNullableNumber(firstValue(source, ["ReturnableQty"], null));

    let resolvedQty;
    if (allowedReturnOpenQty != null) {
        // Align returnable quantity with active requested quantity to prevent overlap.
        resolvedQty = Math.max(0, allowedReturnOpenQty - requestedReturnQty);
    } else if (returnableQty != null) {
        resolvedQty = Math.max(0, returnableQty);
    } else {
        resolvedQty = Math.max(0, baseQty - creditNoteReturnQty - requestedReturnQty);
    }

    if (allowedReturnOpenQty != null && returnableQty != null) {
        resolvedQty = Math.min(resolvedQty, Math.max(0, returnableQty));
    }

    return Math.min(baseQty, resolvedQty);
};

/**
 * Normalize one delivery record into a canonical SO/DN object
 */
const normalizeDeliveryRecord = (record = {}, soLine = {}) => {
    const normalized = {
        raw: record,

        RowType: String(firstValue(record, ["RowType"], "") || "").toUpperCase(),

        SO_DocEntry: firstValue(record, ["SO_DocEntry"], firstValue(soLine, ["SO_DocEntry", "DocEntry"], null)),
        SO_DocNum: firstValue(record, ["SO_DocNum"], firstValue(soLine, ["SO_DocNum", "DocNum"], null)),
        SO_LineNum: firstValue(record, ["SO_LineNum"], firstValue(soLine, ["SO_LineNum", "LineNum"], null)),
        SO_ItemCode: firstValue(record, ["SO_ItemCode"], firstValue(soLine, ["SO_ItemCode", "ItemCode"], null)),
        SO_ItemDesc: firstValue(record, ["SO_ItemDesc"], firstValue(soLine, ["SO_ItemDesc", "Dscription"], null)),
        SO_U_RefItemId: firstValue(record, ["SO_U_RefItemId"], firstValue(soLine, ["SO_U_RefItemId", "U_RefItemId"], null)),
        SO_InventoryItem: firstValue(record, ["SO_InventoryItem"], firstValue(soLine, ["SO_InventoryItem"], null)),
        SO_SalesItem: firstValue(record, ["SO_SalesItem"], firstValue(soLine, ["SO_SalesItem"], null)),

        DN_DocEntry: firstValue(record, ["DN_DocEntry", "DeliveryDocEntry"], null),
        DN_DocNum: firstValue(record, ["DN_DocNum", "DeliveryNoteNo", "DeliveryDocNum"], null),
        DN_DeliveryDate: formatDate(firstValue(record, ["DN_DeliveryDate", "DeliveryDate"], null)),
        DN_LineNum: firstValue(record, ["DN_LineNum", "DeliveryLine"], null),
        DN_ItemCode: firstValue(record, ["DN_ItemCode"], firstValue(record, ["ItemCode"], null)),
        DN_U_RefItemId: firstValue(record, ["DN_U_RefItemId"], null),
        DN_ItemDesc: firstValue(record, ["DN_ItemDesc"], firstValue(record, ["Dscription"], null)),
        DN_WhsCode: firstValue(record, ["DN_WhsCode", "WarehouseCode"], firstValue(soLine, ["WhsCode"], null)),
        DN_LineStatus: firstValue(record, ["DN_LineStatus", "LineStatus"], null),
        DN_DeliveredQty: toNumber(firstValue(record, ["DN_DeliveredQty", "DeliveredQty"], 0)),
        DN_OpenQty: toNumber(firstValue(record, ["DN_OpenQty", "OpenQty", "OpenQtyToDeliver"], 0)),

        DeliveryDocEntry: firstValue(record, ["DeliveryDocEntry", "DN_DocEntry"], null),
        DeliveryNoteNo: firstValue(record, ["DeliveryNoteNo", "DeliveryDocNum", "DN_DocNum"], null),
        DeliveryDate: formatDate(firstValue(record, ["DeliveryDate", "DN_DeliveryDate"], null)),
        DeliveryLine: firstValue(record, ["DeliveryLine", "DN_LineNum"], null),
        DeliveredQty: toNumber(firstValue(record, ["DeliveredQty", "DN_DeliveredQty"], 0)),
        OpenQty: toNumber(firstValue(record, ["OpenQty", "OpenQtyToDeliver", "DN_OpenQty"], 0)),
        AllowReturn: toBoolean(firstValue(record, ["AllowReturn", "allow_return"], null), null),
        AllowSalesReturnRequest: toBoolean(
            firstValue(record, ["AllowSalesReturnRequest"], firstValue(soLine, ["AllowSalesReturnRequest"], null)),
            null
        ),

        SelectableForReturn: firstValue(record, ["SelectableForReturn"], null),
        BOMLevel: firstValue(record, ["BOMLevel"], null),
        BOMRequiredQty: firstValue(record, ["BOMRequiredQty"], firstValue(soLine, ["BOMReqQty"], null)),
    };

    if (!normalized.RowType) {
        normalized.RowType = (normalized.DN_DocEntry != null || normalized.DeliveryDocEntry != null || normalized.DN_DeliveredQty > 0 || normalized.DeliveredQty > 0)
            ? "DELIVERED"
            : "OPEN";
    }

    if (normalized.DeliveredQty === 0 && normalized.DN_DeliveredQty !== 0) {
        normalized.DeliveredQty = normalized.DN_DeliveredQty;
    }
    if (normalized.DN_DeliveredQty === 0 && normalized.DeliveredQty !== 0) {
        normalized.DN_DeliveredQty = normalized.DeliveredQty;
    }

    if (normalized.OpenQty === 0 && normalized.DN_OpenQty !== 0) {
        normalized.OpenQty = normalized.DN_OpenQty;
    }
    if (normalized.DN_OpenQty === 0 && normalized.OpenQty !== 0) {
        normalized.DN_OpenQty = normalized.OpenQty;
    }

    return normalized;
};

/**
 * Normalize all delivery records for one SO line
 */
const normalizeDeliveryRecords = (soLine = {}) => {
    const records = Array.isArray(soLine.DeliveryRecords) ? soLine.DeliveryRecords : [];
    return records.map((record) => normalizeDeliveryRecord(record, soLine));
};

/**
 * Sort delivery records by date, then DN number
 */
const sortDeliveryRecords = (records = []) => {
    return records.slice().sort((a, b) => {
        const dateA = formatDate(a.DN_DeliveryDate || a.DeliveryDate);
        const dateB = formatDate(b.DN_DeliveryDate || b.DeliveryDate);
        if (dateA !== dateB) return dateA.localeCompare(dateB);

        const dnA = String(a.DN_DocNum || a.DeliveryNoteNo || a.DN_DocEntry || a.DeliveryDocEntry || "");
        const dnB = String(b.DN_DocNum || b.DeliveryNoteNo || b.DN_DocEntry || b.DeliveryDocEntry || "");
        return dnA.localeCompare(dnB, undefined, { numeric: true });
    });
};

/**
 * Build a transformed return row with full SO and DN mapping context
 */
const buildReturnRow = ({
    rowKey,
    soLine,
    soContext,
    sourceType,
    displaySource,
    qtyAvailableForReturn,
    orderedQty,
    deliveredQty,
    openQty,
    salesOrderLineId,
    itemCode,
    itemDesc,
    itemRefId,
    itemImage,
    isCollapsed,
    isFullyDelivered,
    deliveryRecord = null,
    relatedDeliveryRecords = [],
    lineDeliveryRecords = [],
}) => {
    const primaryRecord = deliveryRecord || (relatedDeliveryRecords.length === 1 ? relatedDeliveryRecords[0] : null);

    const dnDocEntry = primaryRecord ? (primaryRecord.DN_DocEntry != null ? primaryRecord.DN_DocEntry : primaryRecord.DeliveryDocEntry) : null;
    const dnDocNum = primaryRecord ? (primaryRecord.DN_DocNum != null ? primaryRecord.DN_DocNum : primaryRecord.DeliveryNoteNo) : null;
    const dnDate = primaryRecord ? formatDate(primaryRecord.DN_DeliveryDate || primaryRecord.DeliveryDate) : null;
    const dnLineNum = primaryRecord ? (primaryRecord.DN_LineNum != null ? primaryRecord.DN_LineNum : primaryRecord.DeliveryLine) : null;
    const dnDeliveredQty = primaryRecord ? toNumber(primaryRecord.DN_DeliveredQty || primaryRecord.DeliveredQty || 0) : 0;
    const dnOpenQty = primaryRecord ? toNumber(primaryRecord.DN_OpenQty || primaryRecord.OpenQty || 0) : 0;
    const normalizedSourceType = String(sourceType || "").toUpperCase();
    const soLineStatus = String(soContext.SO_LineStatus || "").toUpperCase();
    const relatedAllowFlags = relatedDeliveryRecords
        .map((record) => toBoolean(record.AllowReturn, null))
        .filter((flag) => flag !== null);
    // An undelivered row is only returnable while the source records still allow it. Falling back
    // to the SO line status alone discarded every server-side block (approved credit notes, SAP's
    // own SelectableForReturn) and left already-returned undelivered qty selectable.
    const rowAllowReturn = (normalizedSourceType === "UNDELIVERED" || normalizedSourceType === "OPEN")
        ? soLineStatus !== "C" && (relatedAllowFlags.length === 0 || relatedAllowFlags.some((flag) => flag === true))
        : (
            relatedAllowFlags.length > 0
                ? relatedAllowFlags.some((flag) => flag === true)
                : toBoolean(firstValue(soLine, ["AllowSalesReturnRequest"], null), true)
        );
    const rowAllowSalesReturnRequest = toBoolean(firstValue(soLine, ["AllowSalesReturnRequest"], null), null);

    const deliveryLines = relatedDeliveryRecords.map((record) => ({
        delivery_doc_entry: record.DN_DocEntry != null ? record.DN_DocEntry : record.DeliveryDocEntry,
        delivery_doc_num: record.DN_DocNum != null ? record.DN_DocNum : record.DeliveryNoteNo,
        delivery_line_num: record.DN_LineNum != null ? record.DN_LineNum : record.DeliveryLine,
        delivery_date: formatDate(record.DN_DeliveryDate || record.DeliveryDate),
        delivery_status: record.DN_LineStatus || record.RowType || null,
        delivered_qty: toNumber(record.DN_DeliveredQty || record.DeliveredQty || 0),
        open_qty: toNumber(record.DN_OpenQty || record.OpenQty || 0),
        row_type: record.RowType || null,
        allow_return: toBoolean(record.AllowReturn, null),
        so_doc_entry: record.SO_DocEntry != null ? record.SO_DocEntry : soContext.SO_DocEntry,
        so_doc_num: record.SO_DocNum != null ? record.SO_DocNum : soContext.SO_DocNum,
        so_line_num: record.SO_LineNum != null ? record.SO_LineNum : soContext.SO_LineNum,
        so_item_code: record.SO_ItemCode != null ? record.SO_ItemCode : soContext.SO_ItemCode,
        dn_item_code: record.DN_ItemCode != null ? record.DN_ItemCode : null,
        dn_item_desc: record.DN_ItemDesc != null ? record.DN_ItemDesc : null,
        dn_ref_item_id: record.DN_U_RefItemId != null ? record.DN_U_RefItemId : null,
        raw: { ...record.raw },
    }));

    return {
        ...soLine,
        __rowKey: rowKey,
        salesOrderLineId,
        itemCode,
        itemDesc,
        itemRefId,
        itemImage,
        sourceType,
        AllowReturn: rowAllowReturn,
        AllowSalesReturnRequest: rowAllowSalesReturnRequest,
        dnNumber: dnDocNum != null ? dnDocNum : (dnDocEntry != null ? dnDocEntry : null),
        dnDate,
        displaySource,
        qtyAvailableForReturn,
        orderedQty,
        deliveredQty,
        openQty,
        __isCollapsed: !!isCollapsed,
        __isFullyDelivered: !!isFullyDelivered,

        SO_DocEntry: soContext.SO_DocEntry,
        SO_DocNum: soContext.SO_DocNum,
        SO_LineNum: soContext.SO_LineNum,
        SO_ItemCode: soContext.SO_ItemCode,
        SO_ItemDesc: soContext.SO_ItemDesc,
        SO_U_RefItemId: soContext.SO_U_RefItemId,
        SO_WhsCode: soContext.SO_WhsCode,
        SO_LineStatus: soContext.SO_LineStatus,
        SO_InventoryItem: soContext.SO_InventoryItem,
        SO_SalesItem: soContext.SO_SalesItem,

        DN_DocEntry: dnDocEntry,
        DN_DocNum: dnDocNum,
        DN_DeliveryDate: dnDate,
        DN_LineNum: dnLineNum,
        DN_ItemCode: primaryRecord ? primaryRecord.DN_ItemCode : null,
        DN_U_RefItemId: primaryRecord ? primaryRecord.DN_U_RefItemId : null,
        DN_ItemDesc: primaryRecord ? primaryRecord.DN_ItemDesc : null,
        DN_WhsCode: primaryRecord ? primaryRecord.DN_WhsCode : null,
        DN_LineStatus: primaryRecord ? primaryRecord.DN_LineStatus : null,
        DN_DeliveredQty: dnDeliveredQty,
        DN_OpenQty: dnOpenQty,

        DeliveryDocEntry: dnDocEntry,
        DeliveryNoteNo: dnDocNum,
        DeliveryDate: dnDate,
        DeliveryLine: dnLineNum,

        DeliveryLines: deliveryLines,
        DeliveryRecords: lineDeliveryRecords.map((record) => ({ ...record.raw })),

        SO_Raw: { ...soLine },
        DN_Raw: primaryRecord ? { ...primaryRecord.raw } : null,
        DN_RawRecords: relatedDeliveryRecords.map((record) => ({ ...record.raw })),
    };
};

const endpointSourceLabel = (rowType) => {
    if (rowType === "INVOICED") return "Invoiced";
    if (rowType === "OPEN") return "Undelivered";
    return "Delivered";
};

const normalizeEndpointReturnRow = (row = {}, index = 0, getItemImageFn = () => "") => {
    const sourceType = String(firstValue(row, ["RowType", "sourceType"], "") || "").toUpperCase();
    const isOpen = sourceType === "OPEN";
    const itemCode = firstValue(row, ["SO_ItemCode", "DN_ItemCode", "ItemCode"], "");
    const itemDesc = firstValue(row, ["SO_ItemDesc", "DN_ItemDesc", "Dscription"], "");
    const itemRefId = firstValue(row, ["SO_U_RefItemId", "DN_U_RefItemId", "U_RefItemId"], "");
    const salesOrderLine = firstValue(row, ["SalesOrderLine", "SO_LineNum", "LineNum"], null);
    const deliveryDocEntry = firstValue(row, ["DeliveryDocEntry", "DN_DocEntry"], null);
    const deliveryDocNum = firstValue(row, ["DeliveryNoteNo", "DN_DocNum", "DocNum"], null);
    const deliveryLine = firstValue(row, ["DeliveryLine", "DN_LineNum"], null);
    const deliveredQty = toNumber(firstValue(row, ["DeliveredQty", "DN_DeliveredQty"], 0));
    const openQty = toNumber(firstValue(row, ["OpenQtyToDeliver", "OpenQty"], 0));
    const soLineStatus = String(firstValue(row, ["SO_LineStatus", "LineStatus"], "") || "").toUpperCase();
    // Quantity already consumed by approved credit notes is deducted server side. Absent on older
    // payloads, in which case the full delivered/open qty stands.
    const returnableQty = toNullableNumber(firstValue(row, ["ReturnableQty", "AllowedReturnOpenQty"], null));
    const baseQtyForReturn = isOpen ? openQty : deliveredQty;
    const qtyAvailableForReturn = returnableQty == null
        ? baseQtyForReturn
        : Math.max(0, Math.min(baseQtyForReturn, returnableQty));
    // What may close an open row: the server's own AllowReturn, or having nothing left to return.
    //
    // SelectableForReturn is deliberately not consulted here. It is SAP's flag and it reads "N" on
    // every line with no delivery note to return against - which is every non-inventory line, since
    // those are invoiced but never delivered. Honouring it made service items permanently
    // unreturnable (SRV-SUR-OPAY on invoice 079289-AMDFC).
    //
    // The case it was added for is still covered, and more precisely: once approved credit notes
    // consume a row the server sets AllowReturn false and ReturnableQty 0, and the quantity cap
    // above turns that into qtyAvailableForReturn 0. A row with nothing left to return is not
    // returnable, whatever the flags say.
    const openAllowFlag = toBoolean(firstValue(row, ["AllowReturn"], null), null);
    const allowReturn = isOpen
        ? soLineStatus !== "C" && openQty > 0 && openAllowFlag !== false && qtyAvailableForReturn > 0
        : toBoolean(firstValue(row, ["AllowReturn", "SelectableForReturn"], false), false);
    const normalized = {
        ...row,
        __rowKey: `return-endpoint-${salesOrderLine == null ? index : salesOrderLine}-${sourceType || "ROW"}-${deliveryDocEntry == null ? "none" : deliveryDocEntry}`,
        sourceType,
        displaySource: endpointSourceLabel(sourceType),
        __rowTypeLabel: endpointSourceLabel(sourceType),
        itemCode,
        itemDesc,
        itemRefId,
        itemImage: getItemImageFn(itemRefId, itemCode),
        ItemCode: itemCode,
        Dscription: itemDesc,
        SO_U_RefItemId: itemRefId,
        default: getItemImageFn(itemRefId, itemCode),
        SalesOrderLine: salesOrderLine,
        DocNum: deliveryDocNum == null ? "" : deliveryDocNum,
        DocEntry: deliveryDocEntry == null ? firstValue(row, ["SalesOrderDocEntry", "SO_DocEntry"], null) : deliveryDocEntry,
        Qty: qtyAvailableForReturn,
        qtyAvailableForReturn,
        OrderedQty: toNumber(firstValue(row, ["OrderedQty"], 0)),
        DeliveredQty: deliveredQty,
        OpenQty: openQty,
        __maxQty: qtyAvailableForReturn,
        __issueQty: qtyAvailableForReturn,
        __returnAllowed: allowReturn,
        AllowReturn: allowReturn,
        ReturnBaseType: firstValue(row, ["ReturnBaseType", "BaseType"], null),
        ReturnBaseEntry: firstValue(row, ["ReturnBaseEntry", "BaseEntry"], null),
        ReturnBaseLine: firstValue(row, ["ReturnBaseLine", "BaseLine"], null),
        DeliveryDocEntry: deliveryDocEntry,
        DeliveryNoteNo: deliveryDocNum,
        DeliveryDate: formatDate(firstValue(row, ["DeliveryDate", "DN_DeliveryDate"], null)),
        DeliveryLine: deliveryLine,
        DeliveryRecords: [row],
        SO_Raw: { ...row },
        DN_Raw: { ...row },
        DN_RawRecords: [{ ...row }],
    };

    if (Array.isArray(row.components)) {
        normalized.components = row.components.map((component, componentIndex) =>
            normalizeEndpointReturnRow(component, `${index}-${componentIndex}`, getItemImageFn)
        );
    }

    return normalized;
};

export const transformReturnEndpointRows = (rows, getItemImageFn = () => "") => {
    if (!Array.isArray(rows)) return [];
    return rows.map((row, index) => normalizeEndpointReturnRow(row, index, getItemImageFn));
};

export const selectReturnRequestRows = (payload = {}, getItemImageFn = () => "", legacyTransform = (rows) => rows) => {
    if (Array.isArray(payload.ReturnData) && payload.ReturnData.length > 0) {
        return transformReturnEndpointRows(payload.ReturnData, getItemImageFn);
    }

    if (Array.isArray(payload.DeliveryData) && payload.DeliveryData.length > 0) {
        return legacyTransform(payload.DeliveryData);
    }

    return legacyTransform(Array.isArray(payload.DocumentLines) ? payload.DocumentLines : []);
};

/**
 * Transform Sales Order lines into Return Request table rows
 *
 * @param {Array} salesOrderLines - Array of SO lines with delivery data
 * @param {Function} getItemImageFn - Function to retrieve item image by ref/code
 * @returns {Array} Transformed rows for return request table
 */
export const transformReturnRequestRows = (salesOrderLines, getItemImageFn = () => "") => {
    if (!Array.isArray(salesOrderLines) || salesOrderLines.length === 0) {
        return [];
    }

    const outputRows = [];

    salesOrderLines.forEach((soLine) => {
        const lineDeliveryRecords = normalizeDeliveryRecords(soLine);
        const deliveredRecords = lineDeliveryRecords.filter(
            (record) => record.RowType === "DELIVERED" || toNumber(record.DeliveredQty || record.DN_DeliveredQty) > 0
        );
        const openRecords = lineDeliveryRecords.filter((record) => record.RowType === "OPEN");

        const deliveredQtyFromRecords = deliveredRecords.reduce((sum, record) => {
            return sum + toNumber(record.DeliveredQty || record.DN_DeliveredQty || 0);
        }, 0);

        const orderedQty = toNumber(firstValue(soLine, ["Quantity", "OrderedQty"], 0));
        const deliveredQtyFromLine = toNumber(firstValue(soLine, ["DelivrdQty", "DeliveredQty"], deliveredQtyFromRecords));
        const deliveredQty = deliveredQtyFromLine > 0 ? deliveredQtyFromLine : deliveredQtyFromRecords;
        const openQty = toNumber(firstValue(soLine, ["OpenQty", "OpenQtyToDeliver"], Math.max(orderedQty - deliveredQty, 0)));

        const salesOrderLineId = firstValue(soLine, ["LineNum", "SO_LineNum"], deliveredRecords.length ? deliveredRecords[0].SO_LineNum : "");
        const itemCode = firstValue(soLine, ["ItemCode", "SO_ItemCode"], "");
        const itemDesc = firstValue(soLine, ["Dscription", "SO_ItemDesc"], "");
        const itemRefId = firstValue(soLine, ["U_RefItemId", "SO_U_RefItemId"], "");
        const itemImage = getItemImageFn(itemRefId, itemCode);

        const soContext = {
            SO_DocEntry: firstValue(
                soLine,
                ["SO_DocEntry", "DocEntry"],
                deliveredRecords.length ? deliveredRecords[0].SO_DocEntry : null
            ),
            SO_DocNum: firstValue(
                soLine,
                ["SO_DocNum", "DocNum"],
                deliveredRecords.length ? deliveredRecords[0].SO_DocNum : null
            ),
            SO_LineNum: firstValue(
                soLine,
                ["SO_LineNum", "LineNum"],
                deliveredRecords.length ? deliveredRecords[0].SO_LineNum : salesOrderLineId
            ),
            SO_ItemCode: firstValue(
                soLine,
                ["SO_ItemCode", "ItemCode"],
                deliveredRecords.length ? deliveredRecords[0].SO_ItemCode : itemCode
            ),
            SO_ItemDesc: firstValue(
                soLine,
                ["SO_ItemDesc", "Dscription"],
                deliveredRecords.length ? deliveredRecords[0].SO_ItemDesc : itemDesc
            ),
            SO_U_RefItemId: firstValue(
                soLine,
                ["SO_U_RefItemId", "U_RefItemId"],
                deliveredRecords.length ? deliveredRecords[0].SO_U_RefItemId : itemRefId
            ),
            SO_WhsCode: firstValue(soLine, ["SO_WhsCode", "WhsCode"], null),
            SO_LineStatus: firstValue(soLine, ["SO_LineStatus", "LineStatus"], null),
            SO_InventoryItem: firstValue(
                soLine,
                ["SO_InventoryItem"],
                deliveredRecords.length ? deliveredRecords[0].SO_InventoryItem : null
            ),
            SO_SalesItem: firstValue(
                soLine,
                ["SO_SalesItem"],
                deliveredRecords.length ? deliveredRecords[0].SO_SalesItem : null
            ),
        };

        const isFullyDelivered = orderedQty > 0 && deliveredQty > 0 && deliveredQty >= orderedQty;
        const deliveredReturnableQty = resolveDeliveredReturnableQty(soLine, deliveredQty);

        if (isFullyDelivered) {
            const sortedDelivered = sortDeliveryRecords(deliveredRecords);
            const hasMultipleDNs = sortedDelivered.length > 1;
            const singleDelivery = sortedDelivered.length === 1 ? sortedDelivered[0] : null;
            const displaySource = hasMultipleDNs ? "Multiple DNs" : "Delivered";
            const collapsedReturnableQty = Math.min(deliveredQty, deliveredReturnableQty);

            outputRows.push(buildReturnRow({
                rowKey: `return-so-${salesOrderLineId}-delivered-collapsed`,
                soLine,
                soContext,
                sourceType: "DELIVERED",
                displaySource,
                qtyAvailableForReturn: collapsedReturnableQty,
                orderedQty,
                deliveredQty,
                openQty: 0,
                salesOrderLineId,
                itemCode,
                itemDesc,
                itemRefId,
                itemImage,
                isCollapsed: true,
                isFullyDelivered: true,
                deliveryRecord: singleDelivery,
                relatedDeliveryRecords: sortedDelivered,
                lineDeliveryRecords,
            }));

            return;
        }

        const sortedDelivered = sortDeliveryRecords(deliveredRecords);
        let remainingDeliveredReturnableQty = deliveredReturnableQty;

        sortedDelivered.forEach((delivery, index) => {
            const deliveredPortion = toNumber(delivery.DeliveredQty || delivery.DN_DeliveredQty || 0);
            if (deliveredPortion <= 0) return;

            const deliveryReturnableCap = resolveDeliveredReturnableQty(delivery, deliveredPortion);
            const effectiveDeliveredPortion = Math.max(
                0,
                Math.min(deliveredPortion, deliveryReturnableCap, remainingDeliveredReturnableQty)
            );

            const dnKey = delivery.DN_DocNum || delivery.DeliveryNoteNo || delivery.DN_DocEntry || delivery.DeliveryDocEntry || "nodn";
            outputRows.push(buildReturnRow({
                rowKey: `return-so-${salesOrderLineId}-delivered-${index}-${dnKey}`,
                soLine,
                soContext,
                sourceType: "DELIVERED",
                displaySource: "Delivered",
                qtyAvailableForReturn: effectiveDeliveredPortion,
                orderedQty,
                deliveredQty: effectiveDeliveredPortion,
                openQty: 0,
                salesOrderLineId,
                itemCode,
                itemDesc,
                itemRefId,
                itemImage,
                isCollapsed: false,
                isFullyDelivered: false,
                deliveryRecord: delivery,
                relatedDeliveryRecords: [delivery],
                lineDeliveryRecords,
            }));

            remainingDeliveredReturnableQty -= effectiveDeliveredPortion;
        });

        if (openQty > 0) {
            outputRows.push(buildReturnRow({
                rowKey: `return-so-${salesOrderLineId}-undelivered`,
                soLine,
                soContext,
                sourceType: "UNDELIVERED",
                displaySource: "Undelivered",
                qtyAvailableForReturn: openQty,
                orderedQty,
                deliveredQty: 0,
                openQty,
                salesOrderLineId,
                itemCode,
                itemDesc,
                itemRefId,
                itemImage,
                isCollapsed: false,
                isFullyDelivered: false,
                deliveryRecord: openRecords.length === 1 ? openRecords[0] : null,
                relatedDeliveryRecords: openRecords,
                lineDeliveryRecords,
            }));
        }
    });

    return outputRows;
};

/**
 * Test scenarios (inline dev harness)
 * Can be enabled via a dev flag for validation
 */
export const runTestScenarios = () => {
    console.group("Return Request Transform Test Scenarios");

    // Scenario A: Item ABC ordered=5, DN001 2026-01-15 qty=2, DN002 2026-01-20 qty=1, remaining undelivered=2
    const scenarioA = [
        {
            LineNum: 0,
            ItemCode: "ABC",
            Dscription: "Item ABC",
            U_RefItemId: "ABC-REF",
            Quantity: 5,
            DelivrdQty: 3,
            OpenQty: 2,
            DeliveryRecords: [
                { DeliveryNoteNo: "DN001", DeliveryDate: "2026-01-15", DeliveredQty: 2, DeliveryDocEntry: 1001, DeliveryLine: 0 },
                { DeliveryNoteNo: "DN002", DeliveryDate: "2026-01-20", DeliveredQty: 1, DeliveryDocEntry: 1002, DeliveryLine: 0 },
            ],
        },
    ];

    const resultA = transformReturnRequestRows(scenarioA);
    console.log("Scenario A (Partial delivery):", resultA);
    console.assert(resultA.length === 3, "Scenario A should have 3 rows");
    console.assert(resultA[0].dnNumber === "DN001", "First row should be DN001");
    console.assert(resultA[1].dnNumber === "DN002", "Second row should be DN002");
    console.assert(resultA[2].sourceType === "UNDELIVERED", "Third row should be UNDELIVERED");
    console.assert(resultA[2].qtyAvailableForReturn === 2, "Undelivered qty should be 2");

    // Scenario B: Item XYZ ordered=10, DN001 2026-01-15 qty=10 (full)
    const scenarioB = [
        {
            LineNum: 1,
            ItemCode: "XYZ",
            Dscription: "Item XYZ",
            U_RefItemId: "XYZ-REF",
            Quantity: 10,
            DelivrdQty: 10,
            OpenQty: 0,
            DeliveryRecords: [
                { DeliveryNoteNo: "DN001", DeliveryDate: "2026-01-15", DeliveredQty: 10, DeliveryDocEntry: 2001, DeliveryLine: 0 },
            ],
        },
    ];

    const resultB = transformReturnRequestRows(scenarioB);
    console.log("Scenario B (Full delivery, single DN):", resultB);
    console.assert(resultB.length === 1, "Scenario B should have 1 row");
    console.assert(resultB[0].__isFullyDelivered === true, "Row should be marked as fully delivered");
    console.assert(resultB[0].dnNumber === "DN001", "Should show DN001");
    console.assert(resultB[0].displaySource === "Delivered", "Source should be 'Delivered'");
    console.assert(resultB[0].qtyAvailableForReturn === 10, "Available qty should be 10");

    // Scenario C: Item DEF ordered=15, DN003 qty=5, DN004 qty=10 (full, multiple DNs)
    const scenarioC = [
        {
            LineNum: 2,
            ItemCode: "DEF",
            Dscription: "Item DEF",
            U_RefItemId: "DEF-REF",
            Quantity: 15,
            DelivrdQty: 15,
            OpenQty: 0,
            DeliveryRecords: [
                { DeliveryNoteNo: "DN003", DeliveryDate: "2026-01-10", DeliveredQty: 5, DeliveryDocEntry: 3001, DeliveryLine: 0 },
                { DeliveryNoteNo: "DN004", DeliveryDate: "2026-01-12", DeliveredQty: 10, DeliveryDocEntry: 3002, DeliveryLine: 0 },
            ],
        },
    ];

    const resultC = transformReturnRequestRows(scenarioC);
    console.log("Scenario C (Full delivery, multiple DNs):", resultC);
    console.assert(resultC.length === 1, "Scenario C should have 1 row (collapsed)");
    console.assert(resultC[0].__isFullyDelivered === true, "Row should be fully delivered");
    console.assert(resultC[0].dnNumber === null, "DN number should be null for multiple DNs");
    console.assert(resultC[0].displaySource === "Multiple DNs", "Should display 'Multiple DNs'");
    console.assert(resultC[0].qtyAvailableForReturn === 15, "Available qty should be 15");

    // Scenario D: DN field format from SAP query (DN_* and SO_*)
    const scenarioD = [
        {
            DocEntry: 2015,
            DocNum: 230000001,
            LineNum: 1,
            ItemCode: "117146",
            Dscription: "AGOSTINO SOFA",
            U_RefItemId: "179981",
            Quantity: "4.000000",
            DelivrdQty: "4.000000",
            OpenQty: "0.000000",
            DeliveryRecords: [
                {
                    RowType: "DELIVERED",
                    SO_DocNum: 230000001,
                    SO_DocEntry: 2015,
                    SO_LineNum: 1,
                    DN_DocNum: 250300567,
                    DN_DocEntry: 622,
                    DN_DeliveryDate: "2025-11-30T00:00:00",
                    DN_LineNum: 0,
                    DN_ItemCode: "117146",
                    DN_U_RefItemId: "179981",
                    DN_ItemDesc: "AGOSTINO SOFA",
                    DN_WhsCode: "WH-01",
                    DN_LineStatus: "O",
                    DN_DeliveredQty: "4.000000",
                    DN_OpenQty: "4.000000",
                },
            ],
        },
    ];

    const resultD = transformReturnRequestRows(scenarioD);
    console.log("Scenario D (SO_*/DN_* fields):", resultD);
    console.assert(resultD.length === 1, "Scenario D should have 1 row");
    console.assert(String(resultD[0].SO_DocEntry) === "2015", "SO_DocEntry should be mapped");
    console.assert(String(resultD[0].DN_DocEntry) === "622", "DN_DocEntry should be mapped");
    console.assert(String(resultD[0].DN_LineNum) === "0", "DN_LineNum should be mapped");
    console.assert(resultD[0].dnNumber === 250300567, "DN number should be mapped from DN_DocNum");

    // Scenario E: invoice 079061-TLDFC - the approved credit note came off the undelivered
    // portion, so the undelivered row must be blocked and the delivered balance stay returnable.
    const scenarioE = [
        {
            RowType: "DELIVERED",
            SalesOrderLine: 1,
            SO_LineStatus: "O",
            SO_ItemCode: "INSS-FAPITE001",
            SO_U_RefItemId: "330994",
            OrderedQty: "9.000000",
            OpenQtyToDeliver: "1.000000",
            DeliveredQty: "8.000000",
            DN_OpenQty: "8.000000",
            DeliveryNoteNo: 260303120,
            DeliveryDocEntry: 5001,
            SelectableForReturn: "Y",
            RequestedReturnQty: "0.000000",
            ReturnableQty: "8.000000",
        },
        {
            RowType: "OPEN",
            SalesOrderLine: 1,
            SO_LineStatus: "O",
            SO_ItemCode: "INSS-FAPITE001",
            SO_U_RefItemId: "330994",
            OrderedQty: "9.000000",
            OpenQtyToDeliver: "1.000000",
            DeliveredQty: "0.000000",
            DN_OpenQty: "0.000000",
            SelectableForReturn: "N",
            RequestedReturnQty: "1.000000",
            ReturnableQty: "0.000000",
        },
    ];

    const resultE = transformReturnEndpointRows(scenarioE);
    console.log("Scenario E (credit note off the undelivered portion):", resultE);
    console.assert(resultE[0].__returnAllowed === true, "Delivered row should stay returnable");
    console.assert(resultE[0].qtyAvailableForReturn === 8, "Delivered returnable qty should be 8");
    console.assert(resultE[1].__returnAllowed === false, "Undelivered row should be blocked");
    console.assert(resultE[1].qtyAvailableForReturn === 0, "Undelivered returnable qty should be 0");

    // Scenario F: payloads without the server flags must not regress to blocked.
    const resultF = transformReturnEndpointRows([
        { RowType: "OPEN", SalesOrderLine: 3, SO_LineStatus: "O", SO_ItemCode: "GHI", OpenQtyToDeliver: "4.000000" },
    ]);
    console.log("Scenario F (no server flags):", resultF);
    console.assert(resultF[0].__returnAllowed === true, "Undelivered row should be allowed without flags");
    console.assert(resultF[0].qtyAvailableForReturn === 4, "Undelivered qty should fall back to open qty");

    console.groupEnd();
};
